
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { GeocodeResult } from '@googlemaps/google-maps-services-js';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';

import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../framework/fields/SwitchField.vue';

import { sharedState } from '../../../../framework/state';
import { getResource, putResource } from '../../../../framework/client/resource';
import { getEnumerationOptions, getGroupOptions } from '../../../../framework/service/options';

import AddDocument from '../document/AddDocument.vue';
import SaveApiKey from './SaveApiKey.vue';

import { CustomerType } from '../../../../../common/application/model/enums/CustomerType';
import { Customer } from '../../../../../common/application/model/Customer';
import { CustomerState } from '../../../../../common/application/model/enums/CustomerState';
import { InvoiceType } from '../../../../../common/application/model/enums/InvoiceType';
import { PaymentTerms } from '../../../../../common/application/model/enums/PaymentTerms';
import { getCustomerOptions } from '../../../service/application_options_service';
import { Iso4217CurrencyCode } from '../../../../../common/application/model/enums/Iso4217CurrencyCode';
import { parseGeocodeResult } from '../../../../../common/application/service/google_maps_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { successToast, errorToast } from '../../../service/toast_service';
import { updateCachedResourceRow } from '../../../service/query_cache_service';
import { getMaponApiKey, saveMaponApiKey } from '../../../client/mapon_client';
import { getGSGApiKey, saveGSGApiKey } from '../../../client/gsg_client';
import { InvoicingLanguage } from '../../../../../common/application/model/enums/InvoicingLanguage';

@Component({
    methods: { getGSGApiKey, getMaponApiKey, saveGSGApiKey, saveMaponApiKey },
    components: {
        SelectField,
        TextField,
        SwitchField,
        GoogleMaps,
        SaveApiKey,
        ValidationObserver,
        AddDocument,
    },
    beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: EditCustomer) => {
            vm.getPrevRouter(from); //get the previous URL
        });
    },
})
export default class EditCustomer extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.CUSTOMER;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as Customer,
        customerTypeOptions: [] as { id: string | undefined; label: string }[],
        customerStateOptions: [] as { id: string | undefined; label: string }[],
        invoiceTypeOptions: [] as { id: string | undefined; label: string }[],
        paymentTermsOptions: [] as { id: string | undefined; label: string }[],
        groupOptions: [] as { id: string | undefined; label: string }[],
        customerOptions: [] as { id: string | undefined; label: string }[],
        currencyOptions: [] as { id: string | undefined; label: string }[],
        invoicingLanguageOptions: [] as { id: string | undefined; label: string }[],
        activeTab: 0,
        prevRoute: '',
    };

    // Functions
    async mounted() {
        this.local.activeTab = this.$route.params.activeTab
            ? Number(this.$route.params.activeTab)
            : Number(localStorage.getItem(`${this.resourceType}_ActiveTab`));
        this.local.customerTypeOptions = getEnumerationOptions('CustomerType', Object.keys(CustomerType), this);
        this.local.customerStateOptions = getEnumerationOptions('CustomerState', Object.keys(CustomerState), this);
        this.local.invoiceTypeOptions = getEnumerationOptions('InvoiceType', Object.keys(InvoiceType), this);
        this.local.paymentTermsOptions = getEnumerationOptions('PaymentTerms', Object.keys(PaymentTerms), this);
        this.local.invoicingLanguageOptions = getEnumerationOptions(
            'InvoicingLanguage',
            Object.keys(InvoicingLanguage),
            this,
        );
        this.local.groupOptions = await getGroupOptions();
        this.local.customerOptions = await getCustomerOptions();
        this.local.row = (await getResource<Customer>(this.resourceType, this.id))!!;
        this.local.customerOptions = this.local.customerOptions.filter((o) => o.id !== this.local.row.id);
        this.local.currencyOptions = getEnumerationOptions(
            'Iso4217CurrencyCode',
            Object.keys(Iso4217CurrencyCode),
            this,
        );
        localStorage.removeItem(`${this.resourceType}_ActiveTab`);
    }

    save() {
        this.saveResource(false);
    }

    saveAndClose() {
        this.saveResource(true);
    }

    private async saveResource(goBack: boolean) {
        try {
            const updatedRow = await putResource<Customer>(this.resourceType, this.id, this.local.row);

            successToast(this, 'message.customerSaved');
            updateCachedResourceRow<Customer>(updatedRow, this.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get fullAddress(): string {
        if (!this.local.row.invoicingAddressStreet) {
            return '';
        }

        return `${this.local.row.invoicingAddressStreet}, ${this.local.row.invoicingAddressZip} ${this.local.row.invoicingAddressCity}`;
    }

    setAddress(geocodeResult: GeocodeResult) {
        const parsedLocation = parseGeocodeResult(geocodeResult);

        this.local.row = {
            ...this.local.row,
            invoicingAddressStreet: parsedLocation.street,
            invoicingAddressZip: parsedLocation.zip,
            invoicingAddressCity: parsedLocation.city,
            invoicingAddressCountry: parsedLocation.country,
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 1;
        }
    }
}
